import fetch, { storeService } from "auth/FetchInterceptor";
import { SendGenericLog } from "model/analytics";

const AnalyticsService = {
  getGeneralData: function (): any {
    return fetch({
      url: "/analytics",
      method: "get",
    });
  },

  getRevenueByDate: function (
    gameId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/${gameId}/revenue`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getGeneralRevenueByDate: function (
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/general/revenue`,
      method: "get",
      params: { startRangeDate: startRangeDate?.toISOString(), endRangeDate: endRangeDate?.toISOString() },
    });
  },
  
  getGeneralRevenueLastYear:function(): any {
    return fetch({
    url: `/analytics/general/revenue/last-year`,
    method: "get",
  });},

  getMonthlyRevenueByGateway: function (): any {
    return fetch({
      url: `/analytics/gateway-revenue`,
      method: "get",
    });
  },

  getItemAnalyticsData: function (
    gameId: string,
    itemIds: string[],
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/${gameId}/items`,
      method: "get",
      params: { startRangeDate, endRangeDate, itemIds },
    });
  },

  getCcuByDate: function (
    gameId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/${gameId}/ccu`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getItemAmountByDate: function (
    gameId: string,
    itemId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/${gameId}/itemQuantity`,
      method: "get",
      params: { itemId, startRangeDate, endRangeDate },
    });
  },

  getNewPlayersRevenueByDate: function (
    startRangeDate?: Date,
    endRangeDate?: Date,
    filteredCampaign?: boolean,
    currency?: boolean,
    startPurchaseDate?: Date,
    endPurchaseDate?: Date
  ): any {
    return fetch({
      url: `/analytics/newUsersRevenue`,
      method: "get",
      params: {
        startRangeDate,
        endRangeDate,
        filteredCampaign,
        currency,
        startPurchaseDate,
        endPurchaseDate,
      },
    });
  },

  getCRMData: function (
    googleToken: string,
    facebookToken: string,
    startRangeDate?: Date,
    endRangeDate?: Date,
    page?: number,
    pageSize?: number
  ): any {
    return fetch({
      url: `/analytics/crm`,
      method: "get",
      params: { startRangeDate, endRangeDate, googleToken, facebookToken, page, pageSize },
    });
  },

  getReports: function (): any {
    return fetch({
      url: "/analytics/reports",
      method: "get",
    });
  },

  sendLog: function (
    gameId: string,
    logType: string,
    userEmail: string,
    additionalInfo?: string
  ): any {
    return fetch({
      url: `/analytics/${gameId}/sendLog`,
      method: "post",
      data: {
        logType,
        userEmail,
        additionalInfo,
      },
    });
  },

  sendGenericLog: function ({
    gameId,
    logType,
    userEmail,
    customProperties
  }: SendGenericLog
  ): any {
    return fetch({
      url: `/analytics/${gameId}/sendGenericLog`,
      method: "post",
      data: {
        logType,
        userEmail,
        customProperties,
      },
    });
  },

  getReport: function (
    gameId: string,
    reportId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/${gameId}/reports/${reportId}`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getPlayerPurchaseReport: function (user: string, server: string): any {
    return storeService({
      url: `/payment/cashdata?id=${user}&server=${server}`,
      method: "get",
    });
  },

  getRevenuePerPersonaData: function (): any {
    return fetch({
      url: "/analytics/revenue/persona",
      method: "get",
    });
  },

  getRoyalArenaLogs: function (gameId: string, StartDate?: Date, EndDate?: Date): any {
    return fetch({
      url: `/analytics/${gameId}/royalArenaLogs`,
      method: "get",
      params: { StartDate, EndDate },
    });
  },

  getActivePlayersPerCountry: function (): any {
    return fetch({
      url: "/analytics/getActivePlayersPerCountry",
      method: "get",
    });
  },

  getPerformanceMetrics: function (startDate: Date, endDate: Date): any {
    return fetch({
      url: "/analytics/performanceMetrics",
      method: "get",
      params: { startDate, endDate },
    });
  },

  getElegibleAccountsForConversion: function (): any {
    return fetch({
      url: "/analytics/sunset/getElegibleAccountsForConversion",
      method: "get",
    });
  },

  getElegibleNPSForConversion: function (): any {
    return fetch({
      url: "/analytics/sunset/getElegibleNPSForConversion",
      method: "get",
    });
  },

  getAccountsConverted: function (): any {
    return fetch({
      url: "/analytics/sunset/getAccountsConverted",
      method: "get",
    });
  },

  getNPSConverted: function (): any {
    return fetch({
      url: "/analytics/sunset/getNPSConverted",
      method: "get",
    });
  },

  getNewAccountsGlobal: function (): any {
    return fetch({
      url: "/analytics/sunset/getNewAccountsGlobal",
      method: "get",
    });
  },

  getInactiveAccountsGlobal: function (): any {
    return fetch({
      url: "/analytics/sunset/getInactiveAccountsGlobal",
      method: "get",
    });
  },

  getActiveAccountsGlobal: function (): any {
    return fetch({
      url: "/analytics/sunset/getActiveAccountsGlobal",
      method: "get",
    });
  },

  getCCUAfterMigration: function (): any {
    return fetch({
      url: "/analytics/sunset/getCCUAfterMigration",
      method: "get",
    });
  },

  getTotalPlayersMigration: function (): any {
    return fetch({
      url: "/analytics/sunset/getTotalPlayersMigration",
      method: "get",
    });
  },

  getOldServersPlayersMigration: function (): any {
    return fetch({
      url: "/analytics/sunset/getOldServersPlayersMigration",
      method: "get",
    });
  },

  getRetentionPlayersMigration: function (): any {
    return fetch({
      url: "/analytics/sunset/getRetentionPlayersMigration",
      method: "get",
    });
  },

  getNPProfileMigration: function (): any {
    return fetch({
      url: "/analytics/sunset/getNPProfileMigration",
      method: "get",
    });
  },

  getMigrationPackagesSent: function (): any {
    return fetch({
      url: "/analytics/sunset/getMigrationPackagesSent",
      method: "get",
    });
  },
  getAllConvertedAccountsName: function (): any {
    return fetch({
      url: "/analytics/sunset/getAllConvertedAccountsName",
      method: "get",
    });
  },
  getGrayList: function (): any {
    return fetch({
      url: "/analytics/gray-list",
      method: "get",
    });
  },
  searchRelatedAccounts: function (accountName: string): any {
    return fetch({
      url: `/analytics/gray-list/associated-accounts/${accountName}`,
      method: "get",
    });
  },
  getAssociatedGrayListAccounts: function (accountName: string): any {
    return fetch({
      url: `/analytics/gray-list/associated-accounts-info/${accountName}`,
      method: "get",
    });
  },
  getDashboardGrayList: function (): any {
    return fetch({
      url: "/analytics/gray-list/data",
      method: "get",
    });
  },
  postGrayList: function (
    { accountsName, user, reason }:
      { accountsName: string[], user: string, reason: string }): any {
    return fetch({
      url: "/analytics/gray-list",
      method: "post",
      data: {
        listAccounts: accountsName,
        user,
        reason
      },
    });
  },
  deleteFromGrayList: function (
    { accountName, user, reason }: { accountName: string, user: string, reason: string }
  ): any {
    return fetch({
      url: `/analytics/gray-list`,
      method: "delete",
      data: { accountName, user, reason },
    });
  },
  getGrayListLogs: function (): any {
    return fetch({
      url: "/analytics/gray-list/logs",
      method: "get",
    });
  },

  getCampaingCost: function (
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/analytics/campaign-cost`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getPersonaInfosGlobalProd: function (page: number, pageSize: number): any {
    return fetch({
      url: `/analytics/get-persona-infos-global-prod/${page}/${pageSize}`,
      method: "get",
    });
  },

  getUserMostBuyPackages: function (accountName: string): any {
    return fetch({
      url: `/analytics/get-user-most-buy-packages/${accountName}`,
      method: "get",
    });
  },

  getNewPersonasByDate: function (
    startDate: Date,
    endDate: Date
  ): any {
    return fetch({
      url: `/analytics/get-new-personas-by-date`,
      method: "get",
      params: { startDate, endDate },
    });
  },

  getReactivatedPersonasByDate: function (
    startDate: Date,
    endDate: Date,
    inactivityDaysThreshold: number
  ): any {
    return fetch({
      url: `/analytics/get-reactivated-personas-by-date`,
      method: "get",
      params: { startDate, endDate, inactivityDaysThreshold },
    });
  },

  getPersonaQtyDiffByDate: function (
    oldDateEnd: string,
    recentDateEnd: string,
    oldDateStart?: string,
    recentDateStart?: string
  ): any {
    return fetch({
      url: `/analytics/get-persona-qty-diff-by-date`,
      method: "get",
      params: { oldDateStart, oldDateEnd, recentDateStart, recentDateEnd },
    });
  },

  getRevenueGoals: function (): any {
    return fetch({
      url: "/analytics/get-revenue-goals",
      method: "get",
    });
  },

  addRevenueGoals: function (
    payload: any
  ): any {
    return fetch({
      url: "/analytics/add-revenue-goals",
      method: "post",
      data: payload,
    });
  },

  getPersonaMetrics: function (): any {
    return fetch({
      url: "/analytics/persona-metrics",
      method: "get",
    });
  },

}

export default AnalyticsService;
